import {CaseDetails, matchCaseDetailsByType} from 'ApplicationEntry/models/CaseDetails';
import {isValidPolicyNumberPaperElectronic} from 'ApplicationEntry/models/EnteredPolicyNumber';
import {
  InvalidGroupCriticalIllness,
  ValidGroupCriticalIllness,
} from 'ApplicationEntry/models/ProductsRiders/CriticalIllness/GroupCriticalIllness';
import {CriticalIllnessPlanType} from 'ApplicationEntry/models/ProductsRiders/PlanTypes';
import States from 'ApplicationEntry/models/States';
import {isNonEmptyString} from 'Core/models/EmptyString';
import React, {useEffect, useState} from 'react';
import {useDispatch} from 'react-redux';
import * as ErrorMessages from '../../ErrorMessages';
import './../ProductRiders.sass';
import {
  Checkbox,
  FormControl,
  FormControlLabel,
  FormHelperText,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from '@material-ui/core';
import PackageSelect from '../PackageSelect';
import * as BAActions from 'ApplicationEntry/ducks/billingAgent';
import * as CIActions from 'ApplicationEntry/ducks/ProductsRiders/criticalIllness';
import useBuildPremiumParameterAutoFill from './useBuildPremiumParameterAutoFill';
import {NonEmptyString, StringUnion} from 'Core/models/EmptyString';
import {ValidGroupCaseDetails} from 'ApplicationEntry/models/GroupCaseDetails';
import {BillingAgent} from 'ApplicationEntry/models/BillingAgent';
import {ApplicantDemographics} from 'ApplicationEntry/models/ApplicantDemographics';
import {getSitusStatefromCaseNumber} from 'ApplicationEntry/ducks/caseDetails';

type CriticalIllnessProps = {
  caseDetails: CaseDetails;
  criticalIllness: ValidGroupCriticalIllness | InvalidGroupCriticalIllness;
  billingAgent: BillingAgent;
  applicantDemographics: ApplicantDemographics;
};

const GroupCriticalIllness = (props: CriticalIllnessProps) => {
  const stateList = States;
  const dispatch = useDispatch();
  const {
    planType,
    productType,
    insuredSC,
    calculatedPremium,
    spouseSC,
    benefitAmount,
    cwa,
    policyNumber,
    replacement,
    section125,
    packageSource,
    cancer,
    recurrence,
    criticalIllnessEnhancement,
    wellnessUnits,
    ciBenefit,
    ciOptionII,
    secondEventCI,
    secondEventCancer,
    chronic90Day,
    suppCIHIV,
    suppCI,
    continuationCoverage,
    chronic365Day,
    returnOfPremium,
    jStamp,
    threeTier,
    packageSourceList,
    lifestyleEnhancement,
    cardiopulmonaryEnhancement,
    skinCancer,
    skinCancerUnit,
    specifiedChronicCI,
    specifiedChronicCIInjury,
    specifiedDiseaseEnhancement,
    secondEvaluationTransportation,
    fixedWellnessUnits,
    variableWellnessUnits,
    wellnessPreventiveCareUnits,
  } = props.criticalIllness;

  const {billingMode} = props.billingAgent;
  const {dateOfBirth} = props.applicantDemographics;
  const {billingCode, situsState, userAuthorizedJStamp} = props.caseDetails as ValidGroupCaseDetails;
  const [planTypeBlurred, setBlurPlanType] = useState(false);
  const [productBlurred, setBlurProduct] = useState(false);
  const [policyNumberBlurred, setPolicyNumberBlurred] = useState(false);
  const showPlanTypeError = planTypeBlurred && planType.key === 'Empty';
  const [planTypeLabel, setPlanTypeLabel] = useState<HTMLLabelElement | null>();
  const [wellnessUnitsLabel, setWellnessUnitsLabel] = useState<HTMLLabelElement | null>();
  const [fixedWellnessLabel, setFixedWellnessLabel] = useState<HTMLLabelElement | null>();
  const [variableWellnessLabel, setVariableWellnessLabel] = useState<HTMLLabelElement | null>();
  const [wellnessPreventiveCareLabel, setWellnessPreventiveCareLabel] = useState<HTMLLabelElement | null>();
  const [smokerLabel, setSmokerLabel] = useState<HTMLLabelElement | null>();
  const [ciBenefitLabel, setCIBenefitLabel] = useState<HTMLLabelElement | null>();
  const [situsStateLabel, setSitusStateLabel] = useState<HTMLLabelElement | null>();
  const showProductError = productBlurred && productType.key === 'Empty';
  const [productLabel, setProductLabel] = useState<HTMLLabelElement | null>();

  const showPolicyNumberError = policyNumberBlurred && !isValidPolicyNumberPaperElectronic(policyNumber) && replacement;

  const handleAutoFillPremiumGrpProducts = useBuildPremiumParameterAutoFill();

  const getHeader = (header1: string, header2: string) => {
    return (
      <>
        <Grid item xs={header2 !== '' ? 6 : 12}>
          <Typography
            variant="subtitle2"
            color="primary"
            className="product-riders-section__policy-type__options__head"
          >
            {header1}
          </Typography>
        </Grid>
        {header2 !== '' ? (
          <Grid item xs={6}>
            <Typography
              variant="subtitle2"
              color="primary"
              className="product-riders-section__policy-type__options__head"
            >
              {header2}
            </Typography>
          </Grid>
        ) : null}
      </>
    );
  };

  const getCheckBoxValuesGrpCI = (value: string): boolean => {
    if (value === 'false' || value === '0' || value === 'N') {
      return false;
    } else if (value === 'true' || value === '1' || value === 'Y') {
      return true;
    } else {
      return false;
    }
  };

  useEffect(() => {
    if (isNonEmptyString(productType)) {
      const caseNum = matchCaseDetailsByType({
        Group: groupApp =>
          groupApp.caseNumber.key === 'Loaded'
            ? groupApp.caseNumber.Data
            : groupApp.caseNumber.key === 'UserAssignedCaseNumber'
            ? groupApp.caseNumber.value
            : '',
        _: () => '',
      })(props.caseDetails);
      dispatch(CIActions.getPackageSourceList(caseNum, productType.value));
    }
  }, [dispatch, props.caseDetails, productType]);

  const getCIBenefitMenuItems = () => {
    const menuItems = [] as JSX.Element[];

    menuItems.push(<MenuItem value="" key="ci-benefit-none" />);

    for (let i = 1; i < 101; i++) {
      menuItems.push(
        <MenuItem value={i} key={`ci-benefit-${i}`}>
          {i}
        </MenuItem>
      );
    }

    return menuItems;
  };

  return (
    <>
      <div className="product-riders-section__policy-type" id="critical-illness-section">
        <form noValidate autoComplete="off" className="product-riders-section__policy-type__form">
          <div className="product-riders-section__policy-type__form__row">
            <Grid container spacing={4}>
              <Grid item xs={12} md={3}>
                <FormControl
                  error={showProductError}
                  variant="outlined"
                  className="product-riders-section__policy-type__form__row--field"
                >
                  <InputLabel ref={setProductLabel} id="product-label">
                    Product Type
                  </InputLabel>
                  <Select
                    labelId="product-label"
                    labelWidth={productLabel?.clientWidth}
                    fullWidth
                    id="product"
                    value={productType.value}
                    onBlur={() => setBlurProduct(true)}
                    onChange={e => {
                      dispatch(CIActions.changeProductType(e.target.value as string));
                      dispatch(CIActions.changeBenefitAmount(0));
                      dispatch(CIActions.changePackageSource(''));
                      dispatch(CIActions.changeWellnessUnits(''));
                    }}
                  >
                    <MenuItem value="GVCIP1">GVCIP1</MenuItem>
                    <MenuItem value="GVCIP2">GVCIP2</MenuItem>
                    <MenuItem value="GVCIFX">GVCIFX</MenuItem>
                    <MenuItem value="GVCIP4">GVCIP4</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              {productType.key === 'NonEmpty' && productType.value === 'GVCIP1' ? (
                <>
                  <Grid item xs={12} md={3}>
                    <FormControl
                      error={showPlanTypeError}
                      variant="outlined"
                      className="product-riders-section__policy-type__form__row--field"
                    >
                      <InputLabel ref={setPlanTypeLabel} id="critical-illness-plantype-label">
                        Plan Type
                      </InputLabel>
                      <Select
                        labelId="critical-illness-label-plantype"
                        labelWidth={planTypeLabel?.clientWidth}
                        id="critical-illness-plantype"
                        value={planType.key === 'Empty' ? '' : planType.value}
                        onBlur={() => setBlurPlanType(true)}
                        onChange={e => {
                          dispatch(CIActions.changePlanType(e.target.value as string));
                          if (billingMode.value !== '') {
                            handleAutoFillPremiumGrpProducts(
                              productType,
                              null,
                              billingMode,
                              NonEmptyString.create(e.target.value as string) as StringUnion,
                              dateOfBirth,
                              insuredSC,
                              spouseSC,
                              packageSourceList,
                              packageSource
                            );
                          } else if (billingCode.value !== '') {
                            handleAutoFillPremiumGrpProducts(
                              productType,
                              null,
                              billingCode,
                              NonEmptyString.create(e.target.value as string) as StringUnion,
                              dateOfBirth,
                              insuredSC,
                              spouseSC,
                              packageSourceList,
                              packageSource
                            );
                          }
                          if (e.target.value !== 'FAMILY') {
                            dispatch(CIActions.changeSpouseSC(''));
                          }
                        }}
                        displayEmpty
                      >
                        {CriticalIllnessPlanType.map(plan => (
                          <MenuItem value={plan.value} key={plan.value}>
                            {plan.key}
                          </MenuItem>
                        ))}
                      </Select>
                      {showPlanTypeError && <FormHelperText>{ErrorMessages.REQUIRED_MESSAGE}</FormHelperText>}
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <FormControl variant="outlined" className="product-riders-section__policy-type__form__row--field">
                      <InputLabel ref={setSmokerLabel} id="critical-illness-insuredsc-label">
                        Insured Smoking Code
                      </InputLabel>
                      <Select
                        labelId="critical-illness-label-insuredsc"
                        labelWidth={smokerLabel?.clientWidth}
                        id="critical-illness-insuredsc"
                        value={insuredSC.key === 'Empty' ? '' : insuredSC.value}
                        onChange={e => {
                          dispatch(CIActions.changeInsuredSC(e.target.value as string));
                          if (billingMode.value !== '') {
                            handleAutoFillPremiumGrpProducts(
                              productType,
                              null,
                              billingMode,
                              planType,
                              dateOfBirth,
                              NonEmptyString.create(e.target.value as string) as StringUnion,
                              spouseSC,
                              packageSourceList,
                              packageSource
                            );
                          } else if (billingCode.value !== '') {
                            handleAutoFillPremiumGrpProducts(
                              productType,
                              null,
                              billingCode,
                              planType,
                              dateOfBirth,
                              NonEmptyString.create(e.target.value as string) as StringUnion,
                              spouseSC,
                              packageSourceList,
                              packageSource
                            );
                          }
                        }}
                        displayEmpty
                      >
                        <MenuItem value="Smoker">Smoker</MenuItem>
                        <MenuItem value="Non Smoker">Non Smoker</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <FormControl
                      variant="outlined"
                      className="product-riders-section__policy-type__form__row--field"
                      disabled={true}
                    >
                      <InputLabel ref={setSmokerLabel} id="critical-illness-spousesc-label">
                        Spouse Smoking Code
                      </InputLabel>
                      <Select
                        labelId="critical-illness-label-spousesc"
                        labelWidth={smokerLabel?.clientWidth}
                        id="critical-illness-spouse-sc"
                        value={spouseSC.key === 'Empty' ? '' : spouseSC.value}
                        onChange={e => {
                          dispatch(CIActions.changeSpouseSC(e.target.value as string));
                          if (billingMode.value !== '') {
                            handleAutoFillPremiumGrpProducts(
                              productType,
                              null,
                              billingMode,
                              planType,
                              dateOfBirth,
                              insuredSC,
                              NonEmptyString.create(e.target.value as string) as StringUnion,
                              packageSourceList,
                              packageSource
                            );
                          } else if (billingCode.value !== '') {
                            handleAutoFillPremiumGrpProducts(
                              productType,
                              null,
                              billingCode,
                              planType,
                              dateOfBirth,
                              insuredSC,
                              NonEmptyString.create(e.target.value as string) as StringUnion,
                              packageSourceList,
                              packageSource
                            );
                          }
                        }}
                        displayEmpty
                        disabled={planType.value === 'FAMILY' || planType.value === 'INDSPS' ? false : true}
                      >
                        <MenuItem value="Smoker">Smoker</MenuItem>
                        <MenuItem value="Non Smoker">Non Smoker</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>

                  <Grid item xs={12} md={3}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          name="checkedB"
                          color="secondary"
                          id="critical-illness-section125"
                          checked={section125}
                          onChange={() => dispatch(CIActions.changeSection125(!section125))}
                        />
                      }
                      label={<Typography>Section 125</Typography>}
                      className="product-riders-section__policy-type__form__row--field"
                    />
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <TextField
                      id="critical-illness-premium"
                      label="Premium"
                      variant="outlined"
                      placeholder="Enter Premium"
                      margin="normal"
                      fullWidth
                      className="product-riders-section__policy-type__form__row--field"
                      value={calculatedPremium.value}
                      onChange={event => {
                        dispatch(CIActions.changePremium(event.target.value as string));
                        dispatch(CIActions.changeCalculatedPremium(event.target.value as string));
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <TextField
                      id="critical-illness-cwa"
                      label="CWA"
                      variant="outlined"
                      placeholder="Enter CWA"
                      margin="normal"
                      fullWidth
                      className="product-riders-section__policy-type__form__row--field"
                      value={cwa.value}
                      onChange={e => dispatch(CIActions.changeCWA(e.target.value as string))}
                    />
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <PackageSelect
                      packageName={packageSource}
                      packageList={packageSourceList}
                      changePackageAction={CIActions.changePackageSource}
                      handlePackageItems={(packageItem: object) => {
                        if (billingMode.value !== '') {
                          handleAutoFillPremiumGrpProducts(
                            productType,
                            packageItem,
                            billingMode,
                            planType,
                            dateOfBirth,
                            insuredSC,
                            spouseSC
                          );
                        } else if (billingCode.value !== '') {
                          handleAutoFillPremiumGrpProducts(
                            productType,
                            packageItem,
                            billingCode,
                            planType,
                            dateOfBirth,
                            insuredSC,
                            spouseSC
                          );
                        }
                        dispatch(CIActions.changeBenefitAmount(packageItem['benefitAmt'] as number));
                        dispatch(CIActions.changeWellnessUnits(packageItem['wellness'] as string));
                        dispatch(
                          CIActions.changeCriticalIllnessEnhancement(
                            packageItem['enhancement'] === null || packageItem['enhancement'] === '0' ? false : true
                          )
                        );

                        if (packageItem['cancer'] === 'Y') {
                          dispatch(CIActions.changeCancer(true));
                        } else {
                          dispatch(CIActions.changeCancer(false));
                        }
                        if (packageItem['recurrence'] === 'Y') {
                          dispatch(CIActions.changeRecurrence(true));
                        } else {
                          dispatch(CIActions.changeRecurrence(false));
                        }
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <TextField
                      id="critical-illness-benefit-amt"
                      label="Benefit Amount"
                      variant="filled"
                      placeholder="Enter Benefit Amount"
                      margin="normal"
                      fullWidth
                      className="product-riders-section__policy-type__form__row--field"
                      value={benefitAmount}
                      onChange={e => dispatch(CIActions.changeBenefitAmount(parseInt(e.target.value, 10)))}
                      disabled
                    />
                  </Grid>

                  <Grid item xs={12} md={3}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          color="secondary"
                          checked={jStamp}
                          onChange={() => dispatch(CIActions.changeJStamp(!jStamp))}
                          name="checkedJStamp"
                          disabled={!userAuthorizedJStamp}
                        />
                      }
                      label={<Typography>J-Stamp</Typography>}
                      className="product-riders-section__policy-type__form__row--field"
                    />
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          name="checkedB"
                          color="secondary"
                          id="critical-illness-replacement"
                          checked={replacement}
                          onChange={() => {
                            setPolicyNumberBlurred(true);
                            dispatch(CIActions.changeReplacement(!replacement));
                          }}
                        />
                      }
                      label={<Typography className="case-details-section__checkbox-label">Replacement</Typography>}
                      className="product-riders-section__policy-type__form__row--field"
                    />
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <TextField
                      id="critical-illness-policy-number"
                      label="Policy Number"
                      variant="outlined"
                      placeholder="Enter Policy Number"
                      margin="normal"
                      fullWidth
                      error={showPolicyNumberError}
                      helperText={showPolicyNumberError ? ErrorMessages.INVALID_POLICY_NUMBER : ''}
                      className="product-riders-section__policy-type__form__row--field"
                      value={policyNumber.key !== 'ValidPolicyNumber' || !replacement ? '' : policyNumber.value.value}
                      onBlur={() => setPolicyNumberBlurred(true)}
                      onChange={e => dispatch(CIActions.changePolicyNumber(e.target.value as string))}
                      disabled={!replacement}
                      inputProps={{
                        maxLength: 10,
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <FormControl variant="outlined" className="product-riders-section__policy-type__form__row--field">
                      <InputLabel ref={setSitusStateLabel} id="disability-situs-state-label">
                        Situs State
                      </InputLabel>
                      <Select
                        labelId="disability-label-situs-state"
                        labelWidth={situsStateLabel?.clientWidth}
                        id="disability-situs-state"
                        value={situsState.value || props.criticalIllness.situsState.value}
                        onChange={e => {
                          dispatch(CIActions.changeSitusState(e.target.value as string));
                          dispatch(getSitusStatefromCaseNumber(''));
                        }}
                        displayEmpty
                      >
                        {stateList.map(stateCode => (
                          <MenuItem key={stateCode} value={stateCode}>
                            {stateCode}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>

                  {getHeader('Options', '')}

                  <Grid item xs={12} md={3}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          color="secondary"
                          checked={cancer === null ? false : cancer}
                          onChange={() => dispatch(CIActions.changeCancer(!cancer))}
                          name="checkedCancer"
                          disabled
                        />
                      }
                      label={<Typography>Cancer</Typography>}
                      className="product-riders-section__policy-type__form__row--field"
                    />
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          color="secondary"
                          checked={recurrence === null ? false : recurrence}
                          onChange={() => dispatch(CIActions.changeRecurrence(!recurrence))}
                          name="checkedRecurrence"
                          disabled
                        />
                      }
                      label={<Typography>Recurrence</Typography>}
                      className="product-riders-section__policy-type__form__row--field"
                    />
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          color="secondary"
                          checked={criticalIllnessEnhancement}
                          onChange={() =>
                            dispatch(CIActions.changeCriticalIllnessEnhancement(!criticalIllnessEnhancement))
                          }
                          name="checkedCriticalIllnessEnhancement"
                          disabled
                        />
                      }
                      label={<Typography>Critical Illness Enhancement</Typography>}
                      className="product-riders-section__policy-type__form__row--field"
                    />
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <FormControl variant="outlined" className="product-riders-section__policy-type__form__row--field">
                      <InputLabel ref={setWellnessUnitsLabel} id="critical-illness-wellness-label">
                        Wellness Units
                      </InputLabel>
                      <Select
                        labelId="critical-illness-label-wellness"
                        labelWidth={wellnessUnitsLabel?.clientWidth}
                        id="critical-illness-wellness"
                        value={
                          wellnessUnits.key === 'Empty' || wellnessUnits.value === null
                            ? ''
                            : parseFloat(wellnessUnits.value)
                        }
                        onChange={e => {
                          dispatch(CIActions.changeWellnessUnits(e.target.value as string));
                        }}
                        displayEmpty
                        disabled
                      >
                        <MenuItem value="1">1</MenuItem>
                        <MenuItem value="3">3</MenuItem>
                        <MenuItem value="4">4</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                </>
              ) : null}

              {productType.key === 'NonEmpty' &&
              (productType.value === 'GVCIP2' || productType.value === 'GVCIFX' || productType.value === 'GVCIP4') ? (
                <>
                  <Grid item xs={12} md={3}>
                    <FormControl
                      error={showPlanTypeError}
                      variant="outlined"
                      className="product-riders-section__policy-type__form__row--field"
                    >
                      <InputLabel ref={setPlanTypeLabel} id="critical-illness-plantype-label">
                        Plan Type
                      </InputLabel>
                      <Select
                        labelId="critical-illness-label-plantype"
                        labelWidth={planTypeLabel?.clientWidth}
                        id="critical-illness-plantype"
                        value={planType.key === 'Empty' ? '' : planType.value}
                        onBlur={() => setBlurPlanType(true)}
                        onChange={e => {
                          dispatch(CIActions.changePlanType(e.target.value as string));
                          if (billingMode.value !== '') {
                            handleAutoFillPremiumGrpProducts(
                              productType,
                              null,
                              billingMode,
                              NonEmptyString.create(e.target.value as string) as StringUnion,
                              dateOfBirth,
                              insuredSC,
                              spouseSC,
                              packageSourceList,
                              packageSource
                            );
                          } else if (billingCode.value !== '') {
                            handleAutoFillPremiumGrpProducts(
                              productType,
                              null,
                              billingCode,
                              NonEmptyString.create(e.target.value as string) as StringUnion,
                              dateOfBirth,
                              insuredSC,
                              spouseSC,
                              packageSourceList,
                              packageSource
                            );
                          }
                          if (e.target.value !== 'FAMILY') {
                            dispatch(CIActions.changeSpouseSC(''));
                          }
                        }}
                        displayEmpty
                      >
                        {CriticalIllnessPlanType.map(plan => (
                          <MenuItem
                            value={plan.value}
                            key={plan.value}
                            disabled={
                              productType.value === 'GVCIP4' && (plan.value === 'INDSPS' || plan.value === 'INDCH')
                            }
                          >
                            {plan.key}
                          </MenuItem>
                        ))}
                      </Select>
                      {showPlanTypeError && <FormHelperText>{ErrorMessages.REQUIRED_MESSAGE}</FormHelperText>}
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <FormControl variant="outlined" className="product-riders-section__policy-type__form__row--field">
                      <InputLabel ref={setSmokerLabel} id="critical-illness-insuredsc-label">
                        Insured Smoking Code
                      </InputLabel>
                      <Select
                        labelId="critical-illness-label-insuredsc"
                        labelWidth={smokerLabel?.clientWidth}
                        id="critical-illness-insuredsc"
                        value={insuredSC.key === 'Empty' ? '' : insuredSC.value}
                        onChange={e => {
                          dispatch(CIActions.changeInsuredSC(e.target.value as string));
                          if (billingMode.value !== '') {
                            handleAutoFillPremiumGrpProducts(
                              productType,
                              null,
                              billingMode,
                              planType,
                              dateOfBirth,
                              NonEmptyString.create(e.target.value as string) as StringUnion,
                              spouseSC,
                              packageSourceList,
                              packageSource
                            );
                          } else if (billingCode.value !== '') {
                            handleAutoFillPremiumGrpProducts(
                              productType,
                              null,
                              billingCode,
                              planType,
                              dateOfBirth,
                              NonEmptyString.create(e.target.value as string) as StringUnion,
                              spouseSC,
                              packageSourceList,
                              packageSource
                            );
                          }
                        }}
                        displayEmpty
                      >
                        <MenuItem value="Smoker">Smoker</MenuItem>
                        <MenuItem value="Non Smoker">Non Smoker</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <FormControl
                      variant="outlined"
                      className="product-riders-section__policy-type__form__row--field"
                      disabled={true}
                    >
                      <InputLabel ref={setSmokerLabel} id="critical-illness-spousesc-label">
                        Spouse Smoking Code
                      </InputLabel>
                      <Select
                        labelId="critical-illness-label-spousesc"
                        labelWidth={smokerLabel?.clientWidth}
                        id="critical-illness-spouse-sc"
                        value={spouseSC.key === 'Empty' ? '' : spouseSC.value}
                        onChange={e => {
                          dispatch(CIActions.changeSpouseSC(e.target.value as string));
                          if (billingMode.value !== '') {
                            handleAutoFillPremiumGrpProducts(
                              productType,
                              null,
                              billingMode,
                              planType,
                              dateOfBirth,
                              insuredSC,
                              NonEmptyString.create(e.target.value as string) as StringUnion,
                              packageSourceList,
                              packageSource
                            );
                          } else if (billingCode.value !== '') {
                            handleAutoFillPremiumGrpProducts(
                              productType,
                              null,
                              billingCode,
                              planType,
                              dateOfBirth,
                              insuredSC,
                              NonEmptyString.create(e.target.value as string) as StringUnion,
                              packageSourceList,
                              packageSource
                            );
                          }
                        }}
                        displayEmpty
                        disabled={planType.value === 'FAMILY' || planType.value === 'INDSPS' ? false : true}
                      >
                        <MenuItem value="Smoker">Smoker</MenuItem>
                        <MenuItem value="Non Smoker">Non Smoker</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          name="checkedB"
                          color="secondary"
                          id="critical-illness-section125"
                          checked={section125}
                          onChange={() => dispatch(CIActions.changeSection125(!section125))}
                        />
                      }
                      label={<Typography>Section 125</Typography>}
                      className="product-riders-section__policy-type__form__row--field"
                    />
                  </Grid>

                  <Grid item xs={12} md={3}>
                    <TextField
                      id="critical-illness-premium"
                      label="Premium"
                      variant="outlined"
                      placeholder="Enter Premium"
                      margin="normal"
                      fullWidth
                      className="product-riders-section__policy-type__form__row--field"
                      value={calculatedPremium.value}
                      onChange={event => {
                        dispatch(CIActions.changePremium(event.target.value as string));
                        dispatch(CIActions.changeCalculatedPremium(event.target.value as string));
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <TextField
                      id="critical-illness-cwa"
                      label="CWA"
                      variant="outlined"
                      placeholder="Enter CWA"
                      margin="normal"
                      fullWidth
                      className="product-riders-section__policy-type__form__row--field"
                      value={cwa.value}
                      onChange={e => dispatch(CIActions.changeCWA(e.target.value as string))}
                    />
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <PackageSelect
                      packageName={packageSource}
                      packageList={packageSourceList}
                      changePackageAction={CIActions.changePackageSource}
                      handlePackageItems={(packageItem: object) => {
                        if (billingMode.value !== '') {
                          handleAutoFillPremiumGrpProducts(
                            productType,
                            packageItem,
                            billingMode,
                            planType,
                            dateOfBirth,
                            insuredSC,
                            spouseSC
                          );
                        } else if (billingCode.value !== '') {
                          handleAutoFillPremiumGrpProducts(
                            productType,
                            packageItem,
                            billingCode,
                            planType,
                            dateOfBirth,
                            insuredSC,
                            spouseSC
                          );
                        }
                        dispatch(CIActions.changeBenefitAmount(packageItem['benefitAmt'] as number));
                        dispatch(CIActions.changeWellnessUnits(packageItem['wellness'] as string));
                        dispatch(CIActions.changeFixedWellness(packageItem['fixedWellnessBenefit'] as string));
                        dispatch(CIActions.changeVariableWellness(packageItem['variableWellnessBenefit'] as string));
                        dispatch(CIActions.changeWellnessPreventiveCare(packageItem['wellnessPreventive'] as string));
                        dispatch(CIActions.changeThreeTier(packageItem['tier3'] as string));
                        dispatch(CIActions.changeSkinCancerUnit(packageItem['skinCancerUnit'] as string));
                        dispatch(
                          CIActions.changeContinuationCoverage(getCheckBoxValuesGrpCI(packageItem['contnOfCoverage']))
                        );
                        dispatch(
                          CIActions.changeReturnOfPremium(getCheckBoxValuesGrpCI(packageItem['returnOfPremium']))
                        );
                        dispatch(CIActions.changeSuppCIHIV(getCheckBoxValuesGrpCI(packageItem['supplementalWithHIV'])));
                        dispatch(CIActions.changeSuppCI(getCheckBoxValuesGrpCI(packageItem['supplementalWithothHIV'])));
                        dispatch(
                          CIActions.changeSecondEventCancer(getCheckBoxValuesGrpCI(packageItem['secondEventCancer']))
                        );
                        dispatch(
                          CIActions.changeCriticalIllnessEnhancement(
                            packageItem['enhancement'] === null || packageItem['enhancement'] === '0' ? false : true
                          )
                        );
                        dispatch(
                          CIActions.changeChronic365Day(getCheckBoxValuesGrpCI(packageItem['chronicIllness365Days']))
                        );
                        dispatch(
                          CIActions.changeChronic90Day(getCheckBoxValuesGrpCI(packageItem['chronicIllness90Days']))
                        );
                        dispatch(CIActions.changeCIBenefit(packageItem['increasingCIBenefit'] as string));

                        dispatch(CIActions.changeCIOptionII(packageItem['cancer'] === 'Y'));

                        dispatch(CIActions.changeSecondEventCI(packageItem['recurrence'] === 'Y'));

                        dispatch(CIActions.changeSkinCancer(packageItem['skinCancer'] === 'Y'));                        
                        dispatch(
                          CIActions.changeCardiopulmonaryEnhancement(packageItem['cardiopulmonaryEnhancement'] === 'Y')
                        );
                        dispatch(CIActions.changeLifestyleEnhancement(packageItem['lifeStyleEnhancement'] === 'Y'));
                        dispatch(
                          CIActions.changeSecondEvaluationTransportation(packageItem['evalTransportation'] === 'Y')
                        );
                        dispatch(
                          CIActions.changeSpecifiedDiseaseEnhancement(packageItem['specifiedDiseaseRider'] === 'Y')
                        );
                        dispatch(CIActions.changeSpecifiedChronicCI(packageItem['specifiedChronicCI'] === 'Y'));
                        dispatch(
                          CIActions.changeSpecifiedChronicCIInjury(packageItem['specifiedChronicCIandInjury'] === 'Y')
                        );
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <TextField
                      id="critical-illness-benefit-amt"
                      label="Benefit Amount"
                      variant="filled"
                      placeholder="Enter Benefit Amount"
                      margin="normal"
                      fullWidth
                      className="product-riders-section__policy-type__form__row--field"
                      value={benefitAmount}
                      onChange={e => dispatch(CIActions.changeBenefitAmount(parseInt(e.target.value, 10)))}
                      disabled
                    />
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          color="secondary"
                          checked={jStamp}
                          onChange={() => dispatch(CIActions.changeJStamp(!jStamp))}
                          name="checkedJStamp"
                          disabled={!userAuthorizedJStamp}
                        />
                      }
                      label={<Typography>J-Stamp</Typography>}
                      className="product-riders-section__policy-type__form__row--field"
                    />
                  </Grid>

                  <Grid item xs={12} md={3}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          name="checkedB"
                          color="secondary"
                          id="critical-illness-replacement"
                          checked={replacement}
                          onChange={() => {
                            setPolicyNumberBlurred(true);
                            dispatch(CIActions.changeReplacement(!replacement));
                          }}
                        />
                      }
                      label={<Typography className="case-details-section__checkbox-label">Replacement</Typography>}
                      className="product-riders-section__policy-type__form__row--field"
                    />
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <TextField
                      id="critical-illness-policy-number"
                      label="Policy Number"
                      variant="outlined"
                      placeholder="Enter Policy Number"
                      margin="normal"
                      fullWidth
                      error={showPolicyNumberError}
                      helperText={showPolicyNumberError ? ErrorMessages.INVALID_POLICY_NUMBER : ''}
                      className="critical-illness-section__form-text-field"
                      value={policyNumber.key !== 'ValidPolicyNumber' || !replacement ? '' : policyNumber.value.value}
                      onBlur={() => setPolicyNumberBlurred(true)}
                      onChange={e => dispatch(CIActions.changePolicyNumber(e.target.value as string))}
                      disabled={!replacement}
                      inputProps={{
                        maxLength: 10,
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <FormControl variant="outlined" className="product-riders-section__policy-type__form__row--field">
                      <InputLabel ref={setSitusStateLabel} id="disability-situs-state-label">
                        Situs State
                      </InputLabel>
                      <Select
                        labelId="disability-label-situs-state"
                        labelWidth={situsStateLabel?.clientWidth}
                        id="disability-situs-state"
                        value={situsState.value || props.criticalIllness.situsState.value}
                        onChange={e => {
                          dispatch(CIActions.changeSitusState(e.target.value as string));
                          dispatch(getSitusStatefromCaseNumber(''));
                        }}
                        displayEmpty
                      >
                        {stateList.map(stateCode => (
                          <MenuItem key={stateCode} value={stateCode}>
                            {stateCode}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                  {productType.value.toUpperCase() !== 'GVCIP4' && (
                    <Grid item xs={12} md={3}>
                      <TextField
                        id="critical-illness-three-tier"
                        label="3-Tier"
                        variant="outlined"
                        placeholder="3-Tier"
                        margin="normal"
                        fullWidth
                        className="product-riders-section__policy-type__form__row--field"
                        value={threeTier.value}
                        onChange={e => dispatch(CIActions.changeThreeTier(e.target.value))}
                        disabled
                      />
                    </Grid>
                  )}

                  {getHeader('Options', '')}

                  <Grid item xs={12} md={3}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          color="secondary"
                          checked={ciOptionII === null ? false : ciOptionII}
                          onChange={() => dispatch(CIActions.changeCIOptionII(!ciOptionII))}
                          name="checkedCIOptionsII"
                          disabled
                        />
                      }
                      label={
                        <Typography>
                          {productType.value === 'GVCIP2' ? 'CI (Option II)' : 'Cancer CI Option'}
                        </Typography>
                      }
                      className="product-riders-section__policy-type__form__row--field"
                    />
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          color="secondary"
                          checked={secondEventCI === null ? false : secondEventCI}
                          onChange={() => dispatch(CIActions.changeSecondEventCI(!secondEventCI))}
                          name="checkedSecondEventCI"
                          disabled
                        />
                      }
                      label={
                        <Typography>
                          {productType.value === 'GVCIP2' ? '2nd Event CI' : 'Reoccurrence of CI Option'}
                        </Typography>
                      }
                      className="product-riders-section__policy-type__form__row--field"
                    />
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          color="secondary"
                          checked={secondEventCancer}
                          onChange={() => dispatch(CIActions.changeSecondEventCancer(!secondEventCancer))}
                          name="checkedSecondEventCancer"
                          disabled
                        />
                      }
                      label={
                        <Typography>
                          {productType.value === 'GVCIP2' ? '2nd Event Cancer (Option II)' : 'Reoccurrence of Cancer'}
                        </Typography>
                      }
                      className="product-riders-section__policy-type__form__row--field"
                    />
                  </Grid>
                  {productType.value.toUpperCase() === 'GVCIP4' && (
                    <>
                      <Grid item xs={12} md={3}>
                        <FormControlLabel
                          control={
                            <Checkbox
                              color="secondary"
                              checked={secondEvaluationTransportation}
                              onChange={() =>
                                dispatch(
                                  CIActions.changeSecondEvaluationTransportation(!secondEvaluationTransportation)
                                )
                              }
                              name="checkedSecondEvaluationTransportation"
                              disabled
                            />
                          }
                          label={<Typography>Second Evaluation, Transportation, Lodging</Typography>}
                          className="product-riders-section__policy-type__form__row--field"
                        />
                      </Grid>
                      <Grid item xs={12} md={3}>
                        <FormControlLabel
                          control={
                            <Checkbox
                              color="secondary"
                              checked={cardiopulmonaryEnhancement}
                              onChange={() =>
                                dispatch(CIActions.changeCardiopulmonaryEnhancement(!cardiopulmonaryEnhancement))
                              }
                              name="checkedCardiopulmonaryEnhancement"
                              disabled
                            />
                          }
                          label={<Typography>Cardiopulmonary Enhancement</Typography>}
                          className="product-riders-section__policy-type__form__row--field"
                        />
                      </Grid>
                      <Grid item xs={12} md={3}>
                        <FormControlLabel
                          control={
                            <Checkbox
                              color="secondary"
                              checked={lifestyleEnhancement}
                              onChange={() => dispatch(CIActions.changeLifestyleEnhancement(!lifestyleEnhancement))}
                              name="checkedLifestyleEnhancement"
                              disabled
                            />
                          }
                          label={<Typography>Lifestyle Enhancement</Typography>}
                          className="product-riders-section__policy-type__form__row--field"
                        />
                      </Grid>
                    </>
                  )}
                  {productType.value.toUpperCase() === 'GVCIP2' && (
                    <Grid item xs={12} md={3}>
                      <FormControlLabel
                        control={
                          <Checkbox
                            color="secondary"
                            checked={chronic90Day}
                            onChange={() => dispatch(CIActions.changeChronic90Day(!chronic90Day))}
                            name="checkedChronic90Day"
                            disabled
                          />
                        }
                        label={<Typography>Chronic 90 Day</Typography>}
                        className="product-riders-section__policy-type__form__row--field"
                      />
                    </Grid>
                  )}

                  {productType.value.toUpperCase() === 'GVCIP4' && (
                    <Grid item xs={12} md={3}>
                      <FormControlLabel
                        control={
                          <Checkbox
                            color="secondary"
                            checked={skinCancer}
                            onChange={() => dispatch(CIActions.changeSkinCancer(!skinCancer))}
                            name="checkedSkinCancer"
                            disabled
                          />
                        }
                        label={<Typography>Skin Cancer</Typography>}
                        className="product-riders-section__policy-type__form__row--field"
                      />
                    </Grid>
                  )}

                  {productType.value.toUpperCase() === 'GVCIP4' && (
                    <Grid item xs={12} md={3}>
                      <FormControlLabel
                        control={
                          <Checkbox
                            color="secondary"
                            checked={specifiedChronicCI}
                            onChange={() => dispatch(CIActions.changeSpecifiedChronicCI(!specifiedChronicCI))}
                            name="checkedSpecifiedChronicCI"
                            disabled
                          />
                        }
                        label={<Typography>Specified Chronic Illness</Typography>}
                        className="product-riders-section__policy-type__form__row--field"
                      />
                    </Grid>
                  )}

                  {productType.value.toUpperCase() === 'GVCIP4' && (
                    <Grid item xs={12} md={3}>
                      <FormControlLabel
                        control={
                          <Checkbox
                            color="secondary"
                            checked={specifiedChronicCIInjury}
                            onChange={() =>
                              dispatch(CIActions.changeSpecifiedChronicCIInjury(!specifiedChronicCIInjury))
                            }
                            name="checkedSpecifiedChronicCIInjury"
                            disabled
                          />
                        }
                        label={<Typography>Specified Chronic Illness or Injury</Typography>}
                        className="product-riders-section__policy-type__form__row--field"
                      />
                    </Grid>
                  )}

                  <Grid item xs={12} md={3}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          color="secondary"
                          checked={suppCIHIV}
                          onChange={() => dispatch(CIActions.changeSuppCIHIV(!suppCIHIV))}
                          name="checkedSuppCIHIV"
                          disabled
                        />
                      }
                      label={
                        <Typography>
                          {productType.value === 'GVCIP4' ? 'Supplemental with HIV' : 'Supp. CI (HIV)'}
                        </Typography>
                      }
                      className="product-riders-section__policy-type__form__row--field"
                    />
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          color="secondary"
                          checked={suppCI}
                          onChange={() => dispatch(CIActions.changeSuppCI(!suppCI))}
                          name="checkedSuppCI"
                          disabled
                        />
                      }
                      label={
                        <Typography>
                          {productType.value === 'GVCIP4' ? 'Supplemental without HIV' : 'Supp. CI'}
                        </Typography>
                      }
                      className="product-riders-section__policy-type__form__row--field"
                    />
                  </Grid>
                  {productType.value.toUpperCase() === 'GVCIP4' && (
                    <Grid item xs={12} md={3}>
                      <FormControlLabel
                        control={
                          <Checkbox
                            color="secondary"
                            checked={specifiedDiseaseEnhancement}
                            onChange={() =>
                              dispatch(CIActions.changeSpecifiedDiseaseEnhancement(!specifiedDiseaseEnhancement))
                            }
                            name="checkedSpecifiedDiseaseEnhancement"
                            disabled
                          />
                        }
                        label={<Typography>Specified Disease Enhancement</Typography>}
                        className="product-riders-section__policy-type__form__row--field"
                      />
                    </Grid>
                  )}
                  {productType.value.toUpperCase() === 'GVCIP2' && (
                    <Grid item xs={12} md={3}>
                      <FormControlLabel
                        control={
                          <Checkbox
                            color="secondary"
                            checked={continuationCoverage}
                            onChange={() => dispatch(CIActions.changeContinuationCoverage(!continuationCoverage))}
                            name="checkedContinuationCoverage"
                            disabled
                          />
                        }
                        label={<Typography>Continuation of Coverage </Typography>}
                        className="product-riders-section__policy-type__form__row--field"
                      />
                    </Grid>
                  )}
                  {productType.value.toUpperCase() === 'GVCIP2' && (
                    <Grid item xs={12} md={3}>
                      <FormControlLabel
                        control={
                          <Checkbox
                            color="secondary"
                            checked={chronic365Day}
                            onChange={() => dispatch(CIActions.changeChronic365Day(!chronic365Day))}
                            name="checkedChronic365Day"
                            disabled
                          />
                        }
                        label={<Typography>Chronic 365 Day</Typography>}
                        className="product-riders-section__policy-type__form__row--field"
                      />
                    </Grid>
                  )}

                  {productType.value.toUpperCase() === 'GVCIP2' && (
                    <Grid item xs={12} md={3}>
                      <FormControlLabel
                        control={
                          <Checkbox
                            color="secondary"
                            checked={returnOfPremium}
                            onChange={() => dispatch(CIActions.changeReturnOfPremium(!returnOfPremium))}
                            name="checkedReturnOfPremium"
                            disabled
                          />
                        }
                        label={<Typography>Return Of Premium</Typography>}
                        className="product-riders-section__policy-type__form__row--field"
                      />
                    </Grid>
                  )}
                  {productType.value !== 'GVCIP4' && (
                    <Grid item xs={12} md={3}>
                      <FormControl variant="outlined" className="product-riders-section__policy-type__form__row--field">
                        <InputLabel ref={setWellnessUnitsLabel} id="critical-illness-wellness-label">
                          Wellness Units
                        </InputLabel>
                        <Select
                          labelId="critical-illness-label-wellness"
                          labelWidth={wellnessUnitsLabel?.clientWidth}
                          id="critical-illness-wellness"
                          value={
                            wellnessUnits.key === 'Empty' || wellnessUnits.value === null
                              ? ''
                              : parseFloat(wellnessUnits.value)
                          }
                          onChange={e => {
                            dispatch(CIActions.changeWellnessUnits(e.target.value as string));
                          }}
                          displayEmpty
                          disabled
                        >
                          <MenuItem value="1">1</MenuItem>
                          <MenuItem value="2">2</MenuItem>
                          <MenuItem value="3">3</MenuItem>
                          <MenuItem value="4">4</MenuItem>
                        </Select>
                      </FormControl>
                    </Grid>
                  )}
                  {productType.value === 'GVCIP4' && (
                    <>
                      <Grid item xs={12} md={3}>
                        <FormControl
                          variant="outlined"
                          className="product-riders-section__policy-type__form__row--field"
                        >
                          <InputLabel ref={setFixedWellnessLabel} id="critical-illness-fixed-wellness-label">
                            Fixed Wellness
                          </InputLabel>
                          <Select
                            labelId="critical-illness-label-fixed-wellness"
                            labelWidth={fixedWellnessLabel?.clientWidth}
                            id="critical-illness-fixed-wellness"
                            value={
                              fixedWellnessUnits.key === 'Empty' || fixedWellnessUnits.value === null
                                ? ''
                                : parseFloat(fixedWellnessUnits.value)
                            }
                            onChange={e => {
                              dispatch(CIActions.changeFixedWellness(e.target.value as string));
                            }}
                            displayEmpty
                            disabled
                          >
                            <MenuItem value="1">1</MenuItem>
                            <MenuItem value="2">2</MenuItem>
                            <MenuItem value="3">3</MenuItem>
                            <MenuItem value="4">4</MenuItem>
                          </Select>
                        </FormControl>
                      </Grid>
                      <Grid item xs={12} md={3}>
                        <FormControl
                          variant="outlined"
                          className="product-riders-section__policy-type__form__row--field"
                        >
                          <InputLabel ref={setVariableWellnessLabel} id="critical-illness-variable-wellness-label">
                            Variable Wellness
                          </InputLabel>
                          <Select
                            labelId="critical-illness-label-variable-wellness"
                            labelWidth={variableWellnessLabel?.clientWidth}
                            id="critical-illness-variable-wellness"
                            value={
                              variableWellnessUnits.key === 'Empty' || variableWellnessUnits.value === null
                                ? ''
                                : parseFloat(variableWellnessUnits.value)
                            }
                            onChange={e => {
                              dispatch(CIActions.changeVariableWellness(e.target.value as string));
                            }}
                            displayEmpty
                            disabled
                          >
                            <MenuItem value="1">1</MenuItem>
                            <MenuItem value="2">2</MenuItem>
                            <MenuItem value="4">4</MenuItem>
                          </Select>
                        </FormControl>
                      </Grid>
                      <Grid item xs={12} md={3}>
                        <FormControl
                          variant="outlined"
                          className="product-riders-section__policy-type__form__row--field"
                        >
                          <InputLabel
                            ref={setWellnessPreventiveCareLabel}
                            id="critical-illness-wellness-preventive-care-label"
                          >
                            Wellness and Preventive Care
                          </InputLabel>
                          <Select
                            labelId="critical-illness-label-wellness-preventive-care"
                            labelWidth={wellnessPreventiveCareLabel?.clientWidth}
                            id="critical-illness-wellness-preventive-care"
                            value={
                              wellnessPreventiveCareUnits.key === 'Empty' || wellnessPreventiveCareUnits.value === null
                                ? ''
                                : parseFloat(wellnessPreventiveCareUnits.value)
                            }
                            onChange={e => {
                              dispatch(CIActions.changeWellnessPreventiveCare(e.target.value as string));
                            }}
                            displayEmpty
                            disabled
                          >
                            <MenuItem value="1">1</MenuItem>
                            <MenuItem value="2">2</MenuItem>
                            <MenuItem value="3">3</MenuItem>
                            <MenuItem value="4">4</MenuItem>
                          </Select>
                        </FormControl>
                      </Grid>
                    </>
                  )}
                  {productType.value.toUpperCase() === 'GVCIP2' && (
                    <Grid item xs={12} md={3}>
                      <FormControl variant="outlined" className="product-riders-section__policy-type__form__row--field">
                        <InputLabel ref={setCIBenefitLabel} id="critical-illness-insuredsc-label">
                          Inc CI Benefit
                        </InputLabel>
                        <Select
                          labelId="critical-illness-label-insuredsc"
                          labelWidth={ciBenefitLabel?.clientWidth}
                          id="critical-illness-insuredsc"
                          value={
                            ciBenefit.key === 'Empty' || ciBenefit.value === null || ciBenefit.value === undefined
                              ? ''
                              : ciBenefit.value
                          }
                          onChange={e => dispatch(CIActions.changeCIBenefit(e.target.value as string))}
                          displayEmpty
                          disabled
                        >
                          {getCIBenefitMenuItems()}
                        </Select>
                      </FormControl>
                    </Grid>
                  )}
                  {productType.value.toUpperCase() === 'GVCIP2' && (
                    <Grid item xs={12} md={3}>
                      <FormControlLabel
                        control={
                          <Checkbox
                            color="secondary"
                            checked={criticalIllnessEnhancement}
                            onChange={() =>
                              dispatch(CIActions.changeCriticalIllnessEnhancement(!criticalIllnessEnhancement))
                            }
                            name="checkedCriticalIllnessEnhancement"
                            disabled
                          />
                        }
                        label={<Typography>Critical Illness Enhancement</Typography>}
                        className="product-riders-section__policy-type__form__row--field"
                      />
                    </Grid>
                  )}
                </>
              ) : null}
            </Grid>
          </div>
        </form>
      </div>
    </>
  );
};

export default GroupCriticalIllness;
